// src/modules/customers/components/CustomerBankSection.jsx
//
// The customer's saved bank accounts. Filled once here and auto-filled into
// walk-in / pledge cases (the primary account by default).
//
// The first save is free. After that the section is locked: admins edit
// directly (logged), everyone else requests a 'Bank Accounts' edit and, once
// approved, makes ONE save. Changes are made to a draft and saved together so
// that one approval covers add + edit + remove + switch-primary.

import React, { useEffect, useState } from 'react';
import {
  Card, Table, Row, Col, Button, Space, Tag, Modal, Form, Input, Select, Checkbox, Upload, Alert, Typography, Popconfirm, message,
} from 'antd';
import {
  BankOutlined, PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined, LockOutlined, UploadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { saveCustomerBankAccounts, lookupIfsc, uploadRawImage } from '../api';
import { RequestEditModal } from './CustomerEditControl';
import PhoneCaptureButton from '../../../components/PhoneCapture/PhoneCaptureButton';

const { Text } = Typography;

const ACCOUNT_TYPES = [
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CURRENT', label: 'Current' },
  { value: 'OTHER', label: 'Other' },
];

export const maskAccount = (n = '') => (n ? `••••${String(n).slice(-4)}` : '—');

const fileRefOf = (r = {}) => ({
  url: r.url, fileId: r.fileId, thumbnailUrl: r.thumbnailUrl, filePath: r.filePath, name: r.name,
});

function AccountModal({ open, initial, customerId, defaultHolder, onCancel, onOk }) {
  const [form] = Form.useForm();
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(initial
      ? { ...initial, confirmAccountNumber: initial.accountNumber }
      : { accountHolderName: defaultHolder, accountType: 'SAVINGS' });
    setProof(initial?.proof || null);
  }, [open, initial, defaultHolder, form]);

  const doLookup = async () => {
    const code = String(form.getFieldValue('ifscCode') || '').trim().toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) return;
    setLookingUp(true);
    try {
      const info = await lookupIfsc(code);
      if (info) form.setFieldsValue({ ifscCode: code, bankName: info.bank, branchName: info.branch });
    } catch (err) {
      messageApi.warning(err?.response?.data?.message || 'IFSC lookup failed — enter bank and branch by hand');
    } finally {
      setLookingUp(false);
    }
  };

  const uploadProof = async (file) => {
    setUploading(true);
    try {
      const res = await uploadRawImage(file, { fileName: file.name, folderId: `/customer-documents/${customerId}/bank` });
      setProof(fileRefOf(res?.data || res));
    } catch (err) {
      messageApi.error(`Upload failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    const { confirmAccountNumber, ...rest } = v;
    onOk({ ...initial, ...rest, ifscCode: rest.ifscCode.trim().toUpperCase(), accountNumber: rest.accountNumber.replace(/\s/g, ''), proof: proof || undefined });
  };

  return (
    <Modal
      title={initial ? 'Edit bank account' : 'Add bank account'}
      open={open}
      onOk={submit}
      onCancel={onCancel}
      okText={initial ? 'Update' : 'Add'}
      destroyOnClose
    >
      {contextHolder}
      <Form form={form} layout="vertical">
        <Form.Item name="accountHolderName" label="Account Holder Name" rules={[{ required: true, whitespace: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="accountNumber"
          label="Account Number"
          rules={[{ required: true }, { pattern: /^\d{9,18}$/, message: '9–18 digits' }]}
          normalize={(v) => (v || '').replace(/\D/g, '')}
        >
          <Input inputMode="numeric" />
        </Form.Item>
        <Form.Item
          name="confirmAccountNumber"
          label="Confirm Account Number"
          dependencies={['accountNumber']}
          normalize={(v) => (v || '').replace(/\D/g, '')}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator: (_, value) => (!value || value === getFieldValue('accountNumber')
                ? Promise.resolve() : Promise.reject(new Error('Account numbers do not match'))),
            }),
          ]}
        >
          <Input inputMode="numeric" onPaste={(e) => e.preventDefault()} />
        </Form.Item>
        <Form.Item
          name="ifscCode"
          label="IFSC Code"
          normalize={(v) => (v || '').toUpperCase()}
          rules={[{ required: true }, { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: 'e.g. SBIN0001234' }]}
        >
          <Input
            maxLength={11}
            onBlur={doLookup}
            suffix={<Button type="link" size="small" icon={<SearchOutlined />} loading={lookingUp} onClick={doLookup}>Look up</Button>}
          />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}><Form.Item name="bankName" label="Bank Name"><Input /></Form.Item></Col>
          <Col span={12}><Form.Item name="branchName" label="Branch"><Input /></Form.Item></Col>
        </Row>
        <Form.Item name="accountType" label="Account Type"><Select options={ACCOUNT_TYPES} /></Form.Item>
        <Form.Item label="Cancelled Cheque / Passbook (optional)">
          <Space direction="vertical" style={{ width: '100%' }}>
            {proof?.url && (
              <Space>
                <a href={proof.url} target="_blank" rel="noopener noreferrer">{proof.name || 'View proof'}</a>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setProof(null)} />
              </Space>
            )}
            <Space wrap>
              <Upload customRequest={({ file }) => uploadProof(file)} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.webp">
                <Button size="small" icon={<UploadOutlined />} loading={uploading}>{proof ? 'Replace' : 'Upload'}</Button>
              </Upload>
              <PhoneCaptureButton
                targetKey="customer.bankProof"
                targetLabel="Cancelled Cheque / Passbook — Customer"
                folderPath={`/customer-documents/${customerId}/bank`}
                recordId={customerId}
                maxFiles={1}
                onFiles={(files) => files?.length && setProof(fileRefOf(files[files.length - 1]))}
                buttonText="Capture from Phone (QR)"
                buttonProps={{ size: 'small' }}
              />
            </Space>
          </Space>
        </Form.Item>
        <Form.Item name="isPrimary" valuePropName="checked">
          <Checkbox>Primary account (auto-fills cases)</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}

const CustomerBankSection = ({ customer, canEdit, isAdmin, editState, reloadEditState, onChanged }) => {
  const saved = customer?.bankAccounts || [];
  const [messageApi, contextHolder] = message.useMessage();
  const [draft, setDraft] = useState(null); // null = viewing
  const [modal, setModal] = useState(null);  // { index } | { index: -1 } for new
  const [saving, setSaving] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const r = editState?.editRequest || { status: 'NONE' };
  // Mirrors the server: free only if bank details were never edited before.
  const firstEntry = saved.length === 0
    && !(editState?.history || []).some((h) => (h.changedFields || []).includes('bankAccounts'));
  const unlocked = r.status === 'UNLOCKED' && (r.fields || []).includes('bankAccounts');
  const mayEdit = canEdit && (firstEntry || isAdmin || unlocked);
  const editing = draft !== null;
  const rows = editing ? draft : saved;

  const startEdit = () => setDraft(saved.map((a) => ({ ...a })));

  const upsert = (acc) => {
    setDraft((prev) => {
      const list = [...(prev || [])];
      if (modal.index === -1) list.push(acc); else list[modal.index] = acc;
      // A new primary demotes the others; the first account is primary by default.
      const pi = acc.isPrimary ? (modal.index === -1 ? list.length - 1 : modal.index) : list.findIndex((a) => a.isPrimary);
      return list.map((a, i) => ({ ...a, isPrimary: i === (pi === -1 ? 0 : pi) }));
    });
    setModal(null);
  };

  const removeAt = (index) => setDraft((prev) => {
    const list = prev.filter((_, i) => i !== index);
    if (list.length && !list.some((a) => a.isPrimary)) list[0] = { ...list[0], isPrimary: true };
    return list;
  });

  const makePrimary = (index) => setDraft((prev) => prev.map((a, i) => ({ ...a, isPrimary: i === index })));

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveCustomerBankAccounts(customer._id, draft);
      messageApi.success(res.message || 'Bank details saved');
      setDraft(null);
      onChanged?.();
      reloadEditState?.();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Account Holder',
      dataIndex: 'accountHolderName',
      render: (v, a) => <Space>{v || '—'}{a.isPrimary && <Tag color="gold">Primary</Tag>}</Space>,
    },
    { title: 'Account No.', dataIndex: 'accountNumber', render: (v) => (editing ? v : maskAccount(v)) },
    { title: 'IFSC', dataIndex: 'ifscCode' },
    { title: 'Bank / Branch', render: (_, a) => [a.bankName, a.branchName].filter(Boolean).join(' — ') || '—' },
    { title: 'Type', dataIndex: 'accountType', render: (v) => ACCOUNT_TYPES.find((t) => t.value === v)?.label || v || '—' },
    {
      title: 'Proof',
      render: (_, a) => (a.proof?.url ? <a href={a.proof.url} target="_blank" rel="noopener noreferrer">View</a> : <Text type="secondary">—</Text>),
    },
    ...(editing ? [{
      title: '',
      width: 130,
      render: (_, a, i) => (
        <Space size={2}>
          {!a.isPrimary && <Button type="text" size="small" icon={<StarOutlined />} title="Make primary" onClick={() => makePrimary(i)} />}
          <Button type="text" size="small" icon={<EditOutlined />} title="Edit" onClick={() => setModal({ index: i })} />
          <Popconfirm title="Remove this account?" onConfirm={() => removeAt(i)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Remove" />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  let extra = null;
  if (canEdit && !editing) {
    if (mayEdit) {
      extra = firstEntry
        ? <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setDraft([]); setModal({ index: -1 }); }}>Add Bank Account</Button>
        : <Button size="small" icon={<EditOutlined />} onClick={startEdit}>Edit Bank Details</Button>;
    } else if (r.status === 'PENDING') {
      extra = <Tag color="orange">Edit request pending</Tag>;
    } else if (r.status !== 'UNLOCKED') {
      extra = <Button size="small" icon={<LockOutlined />} onClick={() => setRequestOpen(true)} disabled={!editState}>Request Change</Button>;
    }
  }

  return (
    <Card size="small" title={<Space><BankOutlined /><span>Bank Details</span></Space>} extra={extra}>
      {contextHolder}
      {unlocked && !isAdmin && !editing && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }} message="Bank details change approved — you can make one save." />
      )}
      {editing && !firstEntry && !isAdmin && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Make all your changes, then save once — the approval covers a single save." />
      )}
      {!editing && firstEntry ? (
        <Text type="secondary">No bank account saved. Once added, the primary account fills in automatically on walk-in and pledge cases.</Text>
      ) : (
        <Table
          size="small"
          rowKey={(a, i) => a._id || `new-${i}`}
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 720 }}
        />
      )}
      {editing && (
        <Space style={{ marginTop: 12 }}>
          <Button icon={<PlusOutlined />} onClick={() => setModal({ index: -1 })}>Add account</Button>
          <Button type="primary" loading={saving} disabled={!draft.length && firstEntry} onClick={save}>Save bank details</Button>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
        </Space>
      )}

      <AccountModal
        open={!!modal}
        initial={modal && modal.index >= 0 ? draft?.[modal.index] : null}
        customerId={customer._id}
        defaultHolder={customer.name}
        onCancel={() => { setModal(null); if (firstEntry && !draft?.length) setDraft(null); }}
        onOk={upsert}
      />
      <RequestEditModal
        open={requestOpen}
        customerId={customer._id}
        state={editState}
        initialFields={['bankAccounts']}
        onClose={() => setRequestOpen(false)}
        onSubmitted={reloadEditState}
      />
    </Card>
  );
};

export default CustomerBankSection;
